import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import LayarSapa, { LAYAR_SAPA_PENDING } from '../../components/LayarSapa';
import { getDashboardStats, setSouvenir } from '../../Api';
import iconTamu from '../../assets/images/icon-tamu.svg';
import iconSouvenir from '../../assets/images/icon-souvenir.svg';
import iconGift from '../../assets/images/icon-gift.svg';
import './Dashboard.css';

/** Small donut used on the stat cards. */
function Donut({ value, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  const r = 26;
  const c = 2 * Math.PI * r;

  return (
    <div className="donut">
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} className="donut__track" />
        <circle
          cx="32"
          cy="32"
          r={r}
          className="donut__value"
          strokeDasharray={`${(pct / 100) * c} ${c}`}
          transform="rotate(-90 32 32)"
        />
      </svg>
      <span className="donut__label">{pct} %</span>
    </div>
  );
}

function StatCard({ icon, title, value, suffix, total, unit, wide, note, breakdown }) {
  return (
    <div className={`stat-card ${wide ? 'stat-card--wide' : ''}`}>
      {total !== undefined && <Donut value={value} total={total} />}
      <div className="stat-card__body">
        <p className="stat-card__title">{title}</p>
        <p className="stat-card__figure">
          <img className="stat-card__icon" src={icon} alt="" />
          <strong>{value.toLocaleString('id-ID')}</strong>
          {suffix && <span className="stat-card__suffix">{suffix}</span>}
          {unit && <span className="stat-card__unit">{unit}</span>}
        </p>
        {breakdown && <p className="stat-card__breakdown">{breakdown}</p>}
        {note && <p className="stat-card__note">{note}</p>}
      </div>
    </div>
  );
}


/**
 * Souvenir card. Souvenirs are handed out and counted by hand rather than
 * derived from attendance, so both figures start at 0 and an usher sets them.
 * Subscribers see it read-only.
 */
function SouvenirCard({ value, total, canEdit, customer, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(String(value));
  const [draftTotal, setDraftTotal] = useState(String(total));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const open = () => {
    setDraftValue(String(value));
    setDraftTotal(String(total));
    setError('');
    setEditing(true);
  };

  const save = async (payload) => {
    try {
      setSaving(true);
      const res = await setSouvenir(customer, payload);
      onSaved(res);
      setEditing(false);
    } catch (err) {
      console.error('Souvenir save error:', err);
      setError('Gagal menyimpan.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stat-card">
      <Donut value={value} total={total} />

      <div className="stat-card__body">
        <p className="stat-card__title">
          Total Souvenir
          {canEdit && !editing && (
            <button type="button" className="stat-card__edit" onClick={open}>
              Ubah
            </button>
          )}
        </p>

        {editing ? (
          <form
            className="gift-edit"
            onSubmit={(e) => {
              e.preventDefault();
              save({ value: draftValue, total: draftTotal });
            }}
          >
            <input
              type="number"
              min="0"
              aria-label="Jumlah souvenir"
              value={draftValue}
              autoFocus
              disabled={saving}
              onChange={(e) => setDraftValue(e.target.value)}
            />
            <span className="gift-edit__sep">/</span>
            <input
              type="number"
              min="0"
              aria-label="Total stok souvenir"
              value={draftTotal}
              disabled={saving}
              onChange={(e) => setDraftTotal(e.target.value)}
            />
            <button type="submit" className="gift-edit__save" disabled={saving}>
              {saving ? '...' : 'Simpan'}
            </button>
            <button
              type="button"
              className="gift-edit__cancel"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Batal
            </button>
          </form>
        ) : (
          <p className="stat-card__figure">
            <img className="stat-card__icon" src={iconSouvenir} alt="" />
            <strong>{value.toLocaleString('id-ID')}</strong>
            <span className="stat-card__suffix">
              / {total.toLocaleString('id-ID')}
            </span>
            <span className="stat-card__unit">Pcs</span>
          </p>
        )}

        {error && <p className="stat-card__note stat-card__note--error">{error}</p>}
      </div>
    </div>
  );
}

/** One "By ..." breakdown: a heading and a grid of count cards. */
function Breakdown({ title, groups, emptyText }) {
  return (
    <>
      <h4 className="dash-subhead">{title}</h4>

      {!groups || groups.length === 0 ? (
        <p className="dash-msg">{emptyText}</p>
      ) : (
        <div className="stat-grid stat-grid--category">
          {groups.map((g) => (
            <div className="stat-card stat-card--category" key={g.kategori}>
              <p className="stat-card__title">{g.kategori}</p>
              <p className="stat-card__figure">
                <img className="stat-card__icon" src={iconTamu} alt="" />
                <strong>{g.hadir.toLocaleString('id-ID')}</strong>
                <span className="stat-card__suffix">
                  / {g.total.toLocaleString('id-ID')}
                </span>
                <span className="stat-card__unit">Undangan</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** "2026-10-02 10:30:00" -> "2 Okt 2026" */
function formatEventDate(raw) {
  if (!raw) return null;
  const d = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return null;

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const layarSapa = localStorage.getItem('selectedLayar') || '';
  const [showSapa, setShowSapa] = useState(
    () => Boolean(layarSapa) && sessionStorage.getItem(LAYAR_SAPA_PENDING) === '1'
  );

  const closeSapa = () => {
    sessionStorage.removeItem(LAYAR_SAPA_PENDING);
    setShowSapa(false);
  };

  const clientName = localStorage.getItem('selectedPostTitle') || '';
  const customer = parseInt(localStorage.getItem('selectedCustomer'), 10);

  const load = useCallback(async () => {
    if (!customer) {
      setError('Client belum dipilih. Silakan login ulang.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Dewasa/anak/titipan are counted server-side over every guest; a
      // check-in with 0 pax and 0 kids is a titipan, not a person.
      setStats(await getDashboardStats(customer));
      setError('');
    } catch (err) {
      console.error('Dashboard stats error:', err);
      setError('Gagal memuat data dashboard.');
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
    {showSapa && <LayarSapa image={layarSapa} onDone={closeSapa} />}
    <AppLayout
      searchValue={search}
      onSearchChange={setSearch}
      onSearchSubmit={() =>
        navigate('/tamu', { state: { q: search.trim() } })
      }
    >
      <header className="dash-head">
        <h2>WELCOME,</h2>
        <h3>{clientName || 'Serenity RSVP'}</h3>
      </header>

      {loading && <p className="dash-msg">Memuat data . . .</p>}

      {!loading && error && (
        <div className="dash-msg dash-msg--error">
          <p>{error}</p>
          <button type="button" onClick={load}>Coba lagi</button>
        </div>
      )}

      {!loading && !error && stats && (
        <>
          <div className="stat-grid stat-grid--primary">
            <StatCard
              icon={iconTamu}
              title="Statistik Undangan"
              value={stats.undangan_hadir}
              suffix={`/ ${stats.undangan_total.toLocaleString('id-ID')}`}
              unit="Und"
              total={stats.undangan_total}
            />
            <StatCard
              icon={iconTamu}
              title="Total Tamu Hadir"
              value={stats.orang_hadir}
              suffix={`/ ${stats.orang_total.toLocaleString('id-ID')}`}
              unit="Orang"
              total={stats.orang_total}
              breakdown={`${stats.dewasa_hadir.toLocaleString('id-ID')} Dewasa \u00b7 ${stats.anak_hadir.toLocaleString('id-ID')} Anak`}
            />
            <StatCard
              icon={iconTamu}
              title="Titipan Undangan"
              value={stats.tamu_titipan}
              unit="Und"
              note="Undangan yang check-in tanpa dewasa maupun anak"
            />
          </div>

          <div className="stat-grid stat-grid--secondary">
            <StatCard
              icon={iconTamu}
              title="Tamu Tambahan"
              value={stats.tamu_tambahan}
              unit="Orang"
              note={
                stats.event_date
                  ? `Tamu yang ditambahkan sejak ${formatEventDate(stats.event_date)}` +
                    (new Date(stats.event_date.replace(' ', 'T')) > new Date()
                      ? ' (acara belum berlangsung)'
                      : '')
                  : 'Isi Akad Calendar pada data client untuk menghitung'
              }
            />
            <SouvenirCard
              value={stats.souvenir_manual ?? 0}
              total={stats.souvenir_total_manual ?? 0}
              canEdit={Boolean(stats.can_edit)}
              customer={customer}
              onSaved={(res) => setStats((prev) => ({ ...prev, ...res }))}
            />
            <StatCard icon={iconGift} title="Total Gift" value={stats.gift} unit="Buah" />
          </div>

          <Breakdown
            title="By Kategori"
            groups={stats.by_kategori}
            emptyText="Belum ada data kategori."
          />

          <Breakdown
            title="By Meja/Group"
            groups={stats.by_meja}
            emptyText="Belum ada data meja."
          />
        </>
      )}
    </AppLayout>
    </>
  );
}

export default Dashboard;
